import { useEffect, useState } from 'react';

import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  FormField,
  InputText,
  SubmitButtonWrapper,
  TextBlock,
  Title,
} from '@skillarea/lib-frontend';
import { formatMoney } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import { getResultErrorMessage } from '../-acquiring-shared';
import { accountAuthGuard } from '../-account-auth-guard';
import styles from '../acquiring.module.scss';

import { AccountLayout } from '~/components/Account/AccountLayout';
import { LayoutCard } from '~/components/LayoutCard/LayoutCard';
import { PromoService } from '~/services/promoService';
import { useUserStore } from '~/stores/userStore';

const promoService = new PromoService();

type TPromocodeSearch = {
  code?: string;
};

type TSubmitOutcome =
  | { type: 'idle' }
  | { type: 'success'; code: string; amountLabel: string }
  | { type: 'error'; message: string };

function normalizeSearchCode(search: TPromocodeSearch): string {
  return typeof search.code === 'string' ? search.code.trim() : '';
}

function isApplyLockedForCode(
  outcome: TSubmitOutcome,
  trimmedCode: string,
): boolean {
  return outcome.type === 'success' && trimmedCode === outcome.code;
}

export const Route = createFileRoute('/account/balance/promocode')({
  component: PromocodePage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
  validateSearch: (search: Record<string, unknown>): TPromocodeSearch => ({
    code: typeof search.code === 'string' ? search.code : undefined,
  }),
});

function PromocodePage() {
  const { t } = useTranslation('billing');
  const search = Route.useSearch();
  const loadUser = useUserStore((state) => state.loadUser);
  const [outcome, setOutcome] = useState<TSubmitOutcome>({ type: 'idle' });

  const promocodeForm = useForm({
    defaultValues: {
      code: normalizeSearchCode(search),
    },
    onSubmit: async ({ value }) => {
      const code = value.code.trim();
      if (!code) {
        setOutcome({ type: 'error', message: t('promocode.codeRequired') });
        return;
      }

      setOutcome({ type: 'idle' });

      const result = await promoService.activatePromoCode(
        { body: { code } },
        { showError: false },
      );

      if (!result.success) {
        setOutcome({
          type: 'error',
          message: getResultErrorMessage(result),
        });
        return;
      }

      const coins =
        typeof result.data?.coins === 'number' ? result.data.coins : 0;

      setOutcome({
        type: 'success',
        code,
        amountLabel: formatMoney(coins, { maxDigits: 0 }),
      });
      await loadUser();
    },
  });

  useEffect(() => {
    const codeFromSearch = normalizeSearchCode(search);
    if (!codeFromSearch) {
      return;
    }

    promocodeForm.setFieldValue('code', codeFromSearch);
  }, [search.code]);

  return (
    <AccountLayout>
      <div className={styles.container}>
        <LayoutCard
          data-testid="account-promocode-page"
          hideTitleDivider
          title={t('promocode.title')}
        >
          <form
            className={styles.formCard}
            data-testid="promocode-form"
            onSubmit={(event) => {
              event.preventDefault();
              void promocodeForm.handleSubmit();
            }}
          >
            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('promocode.description')}
              </TextBlock>
              <FormField form={promocodeForm} name="code">
                <InputText
                  autoComplete="off"
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="promocode-input"
                  placeholder={t('promocode.codePlaceholder')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <promocodeForm.Subscribe
              selector={(state: { values: { code: string } }) =>
                state.values.code
              }
            >
              {(code) => {
                const trimmedCode = code.trim();
                const isApplyLocked = isApplyLockedForCode(
                  outcome,
                  trimmedCode,
                );
                const showSuccess = outcome.type === 'success' && isApplyLocked;
                const showError = outcome.type === 'error';

                return (
                  <>
                    {showError ? (
                      <TextBlock
                        color="error"
                        data-testid="promocode-result-error"
                        size="s"
                      >
                        {outcome.message}
                      </TextBlock>
                    ) : null}

                    {showSuccess ? (
                      <Title
                        color="accent"
                        data-testid="promocode-result-success"
                        size="xs"
                      >
                        {t('promocode.success', {
                          amount: outcome.amountLabel,
                        })}
                      </Title>
                    ) : null}

                    <SubmitButtonWrapper
                      forceDisabled={isApplyLocked ? true : undefined}
                      form={promocodeForm}
                    >
                      <Button
                        className={cx(styles.primaryButton)}
                        color="primary"
                        data-testid="promocode-submit"
                        size="s"
                        type="submit"
                        variant="filled"
                      >
                        {t('promocode.submit')}
                      </Button>
                    </SubmitButtonWrapper>
                  </>
                );
              }}
            </promocodeForm.Subscribe>
          </form>
        </LayoutCard>
      </div>
    </AccountLayout>
  );
}
